<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { BPagination, BOverlay } from 'bootstrap-vue-next';
import { PagingDataSource } from '@integro/core';
import { isString } from 'lodash';

const props = withDefaults(
  defineProps<{
    dataSource: PagingDataSource<any>;
    pageSize?: number;
  }>(),
  {
    pageSize: 10,
  },
);

const route = useRoute();
const router = useRouter();

const isLoading = ref(false);
const isReloading = ref(false);
const dataItems = ref<object[]>([]);
const totalCount = ref(0);
const currentPage = ref<number>(1);
const numberOfPages = ref<number | null>(null);

function getCurrentPage(): number {
  const p = route.query['page'];
  const n = Array.isArray(p) ? p[0] : p;
  return isString(n) ? parseInt(n) : 1;
}

function setCurrentPage(page: number) {
  page = page || 1;

  if (currentPage.value !== page) {
    currentPage.value = page;
    _load();
  }
}

function _load() {
  return props.dataSource.load(currentPage.value).then((result) => {
    numberOfPages.value = Math.ceil(result.totalCount / result.pageSize);
    totalCount.value = result.totalCount;
    dataItems.value = result.data;
  });
}

function load() {
  isLoading.value = true;
  return _load().finally(() => {
    isLoading.value = false;
  });
}

function reload() {
  isReloading.value = true;
  return _load().finally(() => {
    isReloading.value = false;
  });
}

const loading = computed(() => isLoading.value || isReloading.value);

const headerSlotProps = computed(() => ({
  totalCount: totalCount.value,
  reload,
  isReloading: isReloading.value,
  items: dataItems.value,
}));

const slotProps = computed(() => ({
  totalCount: totalCount.value,
  reload,
  isReloading: isReloading.value,
  items: dataItems.value,
}));

watch(
  () => props.dataSource,
  () => {
    if (props.dataSource) {
      load();
    }
  },
);

function onPageChange(page: number) {
  const query = page === 1 ? {} : { page: String(page) };
  router.push({ query });
  setCurrentPage(page);
}

let unsub: (() => void) | null = null;

onMounted(() => {
  unsub = router.afterEach((to) => {
    if (to.path === route.path) {
      const page = getCurrentPage();
      if (currentPage.value !== page) {
        setCurrentPage(page);
      }
    }
  });

  setCurrentPage(getCurrentPage());
});

onBeforeUnmount(() => {
  if (unsub) {
    unsub();
  }
});
</script>

<template>
  <div class="d-flex flex-column overflow-hidden">
    <div class="card d-flex flex-column h-100 overflow-hidden">
      <div class="card-header">
        <slot name="header" v-bind="headerSlotProps"></slot>
      </div>

      <div class="card-body h-100 overflow-auto">
        <slot v-bind="slotProps"></slot>
      </div>
    </div>

    <nav class="p-3" aria-label="Paginator navigation">
      <BPagination
        v-if="numberOfPages"
        align="center"
        :model-value="currentPage"
        :total-rows="totalCount"
        :per-page="pageSize"
        @update:model-value="onPageChange"
      />
    </nav>

    <slot name="loading-indicator" :loading="loading">
      <BOverlay
        :show="loading"
        variant="light"
        spinner-variant="primary"
        :opacity="0.3"
        blur=""
        no-wrap
      >
        <strong>Loading</strong>
      </BOverlay>
    </slot>
  </div>
</template>
